<!----------------- BEGIN TS ---------------------->
<script lang="ts">
import { VideoSource } from "@/types";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import UTC from "dayjs/plugin/utc";
import { initPopover } from "@/plugins/bootstrapSetup";

export type CommonComplianceFields = {
  id: string; // A Unique identifier for this event
  cursor: string; // The cursor type to show when hovering over the event
  expandFunction: () => void; // The function to call when the event is clicked
  customClasses: string; // Any custom classes to add to the event
  resultsUrl?: string; // Link to the Visual Agents results
  // Required fields, otherwise don't display event
  timestampLinkable?: string; // will have the raw timestamp OR undefined
  date?: string; // The date converted to a dayjs string as specified by the dateFormat below
  compliant: string; // "compliant" or "non-compliant"
  eventHumanReadable: string; // "compliant" or "noncompliant" display text
  overflowEventTitle?: string; // The title to show on the overflow title
  collapsed: boolean; // Whether or not to show the message or if it is a collapsed message that should be hidden
};
export interface ComplianceResultsInfo extends CommonComplianceFields {
  displayText: string;
  title: string;
}

@Component({})
export default class TheComplianceLogger extends Vue {
  // ---------- Props ----------

  /** The metadata associated with this event. */
  @Prop() videoSourcesWithMetaData!: VideoSource[];

  /** Whether or not the user is hovering over the viewer. */
  @Prop() hovering!: boolean;

  /** Whether or not to display the component. */
  @Prop() showComplianceLogger!: boolean;

  /** The current query in the camio search bar. */
  @Prop() currentQuery!: string;

  /** Tells us if the videos are still loading. */
  @Prop() videosLoaded!: boolean;

  /** The list of labels as a set */
  @Prop() labelsAsSet!: string[];

  // ------- Local Vars --------
  /** The format to put the date in (matches camio_viewer.js currently) */
  dateFormat = "h:mm:ssa"; // See: https://day.js.org/docs/en/display/format

  /** The regex that captures the compliant/non-compliant label */
  complianceRegex = /^(compliant|noncompliant)$/i;
  complianceLabelFound = false;
  compliant?: boolean = undefined;
  resultsLabelFound = false;
  resultsUrl?: string = undefined;
  /** The regex that captures Visual Agents recommended messages */
  recommendedMessageRegex = /^_vs_rm:(.*)$/i;
  /** The regex that captures Visual Agents compliance assessments */
  policyAssessmentRegex = /^_vs_pa:(compliant|noncompliant):(.*)$/i;
  /** The regex that captures Visual Agents results url */
  resultsResultUrlRegex = /^_vs_result:(.*)$/i;

  /** Whether or not the control logs are showing. */
  isExpanded = false;

  /** Used so we can set a timeout on local hover changes so things aren't so abrupt. */
  localHoverForTimeout = false;

  /** Wait before changing the hover state to false */
  HOVER_TIMEOUT = 2000;

  /** Shows a checkmark on successful copy. */
  successfulCopy = false;
  successfulCopyIndex = -1;

  // --------- Watchers --------

  @Watch("showComplianceLogger")
  showLogsChange() {
    if (!this.hovering) {
      this.localHoverForTimeout = true;
      setTimeout(() => {
        this.localHoverForTimeout = false;
      }, this.HOVER_TIMEOUT);
    }
  }

  @Watch("hovering")
  hoverChange(newState: boolean) {
    this.localHoverForTimeout = newState;
  }

  // ------- Lifecycle ---------
  created() {
    // Called when a component is initialized
  }

  mounted() {
    // Called when the component is mounted on a page
    // all this does is forces the getter to run on init
    if (this.$refs.complianceLogger) {
      initPopover(this.$refs.complianceLogger as Element, undefined, false);
    }
    if (this.$refs.overallCompliance) {
      initPopover(this.$refs.overallCompliance as Element, undefined, false);
    }
  }

  // --------- Methods ---------

  /** Finds all labels that match the passed in regex expression */
  findLabelWithRegex(labels: string[], regex: RegExp) {
    const relevantLabels = labels
      .map((label) => {
        const matches = regex.exec(label);
        if (matches) {
          return matches;
        } else {
          return undefined;
        }
      })
      .filter((val): val is RegExpExecArray => !!val);
    return relevantLabels;
  }

  /** Converts a string to title/capital case when it is delimited by spaces. */
  convertToUpperCase(str: string | undefined): string | undefined {
    return str
      ? str
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : undefined;
  }

  /** Makes a dot or underscore seperated string into capitalized words for displaying. */
  makeWords(str: string): string {
    // Handle special case for emails
    const findEmailRegex = /.*@.+\..+/;
    const emailMatches = findEmailRegex.exec(str);
    if (emailMatches) {
      const email = emailMatches[0];
      return email;
    } else {
      return this.convertToUpperCase(
        str.replaceAll(".", " ").replaceAll("_", " ")
      ) as string;
    }
  }

  /** Expands the compliance logs to show all of them. */
  expandToggle(): void {
    this.isExpanded = true;
  }

  /** Shows compliance logs truncated to two. */
  contractToggle(): void {
    this.isExpanded = false;
  }

  /** Checks if we can link this time, and if we can, go to it by emitting an event. */
  goToTimestamp(timestamp: string | undefined): void {
    if (timestamp && this.videosLoaded) {
      this.$emit("jump-to-timestamp", timestamp);
    }
  }

  /** Creates a link based on the current query and the label */
  makeLabelLink(
    label: string | undefined,
    searchJustId: boolean,
    rawLabel?: string
  ): string {
    if (!label) {
      return "";
    }
    const urlBase = window.location.origin;
    let idToSearch = label.replaceAll("_", " ");
    let query = this.currentQuery;
    if (this.currentQuery) {
      // remove the labels we are about to add, note that we don't need to escape the "
      query = query.replaceAll(new RegExp(`"?${rawLabel}"?[ ]*`, "g"), " ");
      query = query.replaceAll(new RegExp(`"?${idToSearch}"?[ ]*`, "g"), " ");
    }

    let newQuery = "";

    if (searchJustId) {
      newQuery = `"${idToSearch}"`;
    } else {
      newQuery = `"${rawLabel}"${query ? " " + query : ""}`;
    }
    return `${urlBase}/app/#search;q=${encodeURIComponent(newQuery)}`;
  }

  /** V2 of makeLabelLink which takes in the formattedThingToSearch - which should be formatted in exactly the way you want it to be searched, and whether or not to append to current query and returns a full link. */
  makeLink(
    formattedThingToSearch: string | undefined,
    appendToQuery: boolean
  ): string | undefined {
    if (!formattedThingToSearch) {
      return undefined;
    }

    const urlBase = window.location.origin;
    let query = this.currentQuery;
    if (this.currentQuery) {
      // remove the labels we are about to add, note that we don't need to escape the "
      query = query.replaceAll(
        new RegExp(`"?${formattedThingToSearch}"?[ ]*`, "g"),
        " "
      );
    }

    let newQuery = "";

    if (appendToQuery) {
      newQuery = `"${formattedThingToSearch}"${query ? " " + query : ""}`;
    } else {
      newQuery = `"${formattedThingToSearch}"`;
    }
    return `${urlBase}/app/#search;q=${encodeURIComponent(newQuery)}`;
  }

  /** Tells us when to make something linkable or not and returns the string value if so. Otherwise returns an empty string (falsy). */
  getComplianceSearchType(complianceObject: ComplianceResultsInfo) {
    return "";
  }

  /** If there is more than 1, for ui reasons, we want to truncate the array of compliance events.
   * Note, this just sets collapsed to true if they should't show so we can still set the popovers.  */
  truncateComplianceArray(
    complianceToDisplay: (ComplianceResultsInfo | ComplianceResultsInfo)[]
  ) {
    if (complianceToDisplay.length > 1 && !this.isExpanded) {
      const extraEvents = complianceToDisplay.length - 1;
      // Iterate through the complianceToDisplay and set collapsed to true starting after index 1 (leave first two)
      const truncated = complianceToDisplay.map((compliance, index) => {
        if (index > 0) {
          compliance.collapsed = true;
        }
        return compliance;
      });
      // const remainingInfo = complianceToDisplay
      //   .slice(2)
      //   .reduce((prev, curr, index) => {
      //     prev.date = curr.date;
      //     prev.id = curr.id;
      //     return prev;
      //   }, {} as { [key: string]: string });

      const fillerEvent = {
        id: "filler-event",
        collapsed: false,
        overflowEventTitle: `+ ${extraEvents} more event${
          extraEvents !== 1 ? "s" : ""
        }`,
        event: "",
        cursor: "pointer",
        expandFunction: this.expandToggle,
        customClasses: "is-dark",
        displayText: "",
        title: "",
        compliant: "",
        eventHumanReadable: "",
      };
      truncated.push(fillerEvent);

      return truncated;
    } else {
      return complianceToDisplay;
    }
  }

  /** Returns the compliance object that contains the desired label */
  complianceObjectsWithLongLabel(
    overallCompliance: string,
    resultsUrl: string,
    allMatchingLabels: RegExpExecArray[],
    title: string
  ): ComplianceResultsInfo[] {
    // dayjs.extend(timezone);
    // dayjs.extend(UTC);

    const fullInfoComplianceObjects: ComplianceResultsInfo[] = allMatchingLabels
      .map((regexMatch, index) => {
        // This is in the format
        // _vs_rm:{{text}}
        // _vs_pa:{{compliant|noncomplaint}}:{{text}}
        const [compliant, message] = regexMatch.slice(1);
        // const rawTimestamp = timestamp.toUpperCase();
        // const date = dayjs(rawTimestamp)
        //   .tz(dayjs.tz.guess(), true)
        //   .format(this.dateFormat);

        if (message === "null" || !message) {
          return undefined;
        }

        return {
          id: `${index}-message`, // A Unique identifier for this event
          collapsed: false, // Whether or not to show the event or if it is a collapsed event that should be hidden
          cursor: this.isExpanded ? "pointer" : "default", // The cursor type to show when hovering over the event
          expandFunction: this.contractToggle, // The function to call when the event is clicked
          customClasses: "", // Any custom classes to add to the event
          // Required fields, otherwise don't display event
          eventHumanReadable: this.makeWords(compliant), // The event type converted to a human readable string (Compliant or Noncompliant)
          compliant:
            compliant != undefined
              ? compliant.toUpperCase()
              : overallCompliance,
          resultsUrl: resultsUrl,
          displayText: message,
          title: title,
        } as ComplianceResultsInfo;
      })
      .filter(
        (complianceObj): complianceObj is ComplianceResultsInfo =>
          !!complianceObj
      );

    const sortedCompliance = fullInfoComplianceObjects;

    // If there were no access control events found, then let the parent know not to show the button
    if (sortedCompliance.length === 0 && !this.complianceLabelFound) {
      this.$emit("hide-access-control-button");
    }

    return this.truncateComplianceArray(
      sortedCompliance
    ) as ComplianceResultsInfo[];
  }

  /** Get full info compliance objects */
  get fullInfoComplianceObjects() {
    const policyAssessmentLabels = this.findLabelWithRegex(
      this.labelsAsSet,
      this.policyAssessmentRegex
    );
    const recommendedMessageLabels = this.findLabelWithRegex(
      this.labelsAsSet,
      this.recommendedMessageRegex
    );
    const allMatchingComplianceLabels = this.findLabelWithRegex(
      this.labelsAsSet,
      this.complianceRegex
    );
    const allMatchingResultsLabels = this.findLabelWithRegex(
      this.labelsAsSet,
      this.resultsResultUrlRegex
    );
    const containsNoncompliant = (arr: any[]) =>
      arr.some((match: string[]) => match[0].toLowerCase() === "noncompliant");
    // Handles displaying the ! Noncompliant vs Compliant header, only if a matching label is found
    // Message labels will display regardless
    if (allMatchingComplianceLabels.length > 0) {
      this.complianceLabelFound = true;
      this.compliant = !containsNoncompliant(allMatchingComplianceLabels);
    }
    if (allMatchingResultsLabels.length > 0) {
      this.resultsLabelFound = true;
      this.resultsUrl = allMatchingResultsLabels[0][1]; // NOTE: Currently only supports one results url label, with the assumption that all of the compliance results for the event are available at that url
    }
    const resultsUrl = this.resultsUrl ? this.resultsUrl : "";
    const complianceText = this.compliant ? "compliant" : "noncompliant"; // If no compliance label this will default to non-compliant
    const policyAssessmentComplianceObjects =
      this.complianceObjectsWithLongLabel(
        complianceText,
        resultsUrl,
        policyAssessmentLabels,
        "Policy Assessment"
      );

    return policyAssessmentComplianceObjects;
    // The below code can be used to display both the policy assessment and the recommended messages
    // const recommendedMessageComplianceObjects =
    //   this.complianceObjectsWithLongLabel(
    //     complianceText,
    //     resultsUrl,
    //     recommendedMessageLabels,
    //     "Recommended Message"
    //   );
    // const allComplianceObjects = policyAssessmentComplianceObjects.concat(
    //   recommendedMessageComplianceObjects
    // );
    // return allComplianceObjects;
  }

  /** Copies the passed in text to the clipboard */
  copyToClipboard(text: string, index: number) {
    if (navigator && navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.successfulCopy = true;
          this.successfulCopyIndex = index;
          setTimeout(() => {
            this.successfulCopy = false;
            this.successfulCopyIndex = -1;
          }, 2000);
        })
        .catch((err) => {
          console.error("Error copying text to clipboard: ", err);
        });
    } else {
      console.log("clipboard copy not supported by browser");
    }
  }
}
</script>
<!----------------- END TS ------------------------>

<!----------------- BEGIN HTML -------------------->
<template lang="html">
  <div
    class="compliance-logger-container-full"
    :class="{
      'fade-in': localHoverForTimeout,
      'fade-out': !localHoverForTimeout,
    }"
    v-if="showComplianceLogger"
  >
    <div
      class="overall-compliance"
      v-show="complianceLabelFound"
      ref="overallCompliance"
    >
      <div>
        <span v-if="!compliant">
          <b-icon icon="exclamation-lg" color="red"></b-icon>
          Noncompliant
        </span>
        <span v-else>
          <b-icon icon="check-lg" color="green"></b-icon>
          Compliant
        </span>
      </div>
      <b-icon v-if="resultsLabelFound && !!resultsUrl" icon="dot"></b-icon>
      <div v-show="resultsLabelFound && !!resultsUrl" class="link">
        <!-- NOTE: Overlay container has ID access-logger-container -->
        <a
          :href="resultsUrl"
          target="_blank"
          data-bs-toggle="popover"
          data-bs-trigger="hover"
          data-bs-container="#access-logger-container"
          data-bs-placement="right"
          title="View Visual Agents results"
        >
          <span>View Results</span>
        </a>
      </div>
    </div>
    <!-- <span class="overall-compliance" v-if="resultsLabelFound">
    </span> -->
    <div class="compliance-logger-container" ref="complianceLogger">
      <!-- NEW Version of compliance. To become the default after sufficient time has passed! -->
      <template>
        <div
          class="compliance-control-item"
          v-for="(
            complianceObject, complianceIndex
          ) in fullInfoComplianceObjects"
          v-show="!complianceObject.collapsed"
          :key="`compliance-control-${complianceObject.id}`"
          :style="{ cursor: complianceObject.cursor }"
          :class="complianceObject.customClasses"
          @click.stop="complianceObject.expandFunction()"
        >
          <div class="full-info-label-list">
            <template v-if="complianceObject.displayText">
              <div>
                <span
                  :class="
                    'badge ' +
                    (complianceObject.compliant == 'COMPLIANT'
                      ? 'bg-success'
                      : 'bg-danger')
                  "
                  >{{ complianceObject.compliant }}</span
                >
                {{ complianceObject.title }}
              </div>

              <div class="compliance-rm compliance-control-row">
                {{ complianceObject.displayText }}
              </div>
            </template>
            <template v-else>
              <div class="overflow-title">
                {{ complianceObject.overflowEventTitle }}
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<!----------------- END HTML ---------------------->

<!----------------- BEGIN SCSS -------------------->
<style scoped lang="scss">
// Override SCSS Classes here
$base-opacity: 0.1;
$top-opacity: 0.7;

.compliance-logger-container-full {
  --bs-success-rgb: 80, 181, 54 !important;
  --bs-bg-opacity: 1 !important;
  --bs-danger-rgb: 220, 53, 69 !important;
  --bs-border-radius-pill: 50rem !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  z-index: 1;
  left: 10px;
  bottom: 30px;
  max-height: 40%;
  max-width: 27%;
  font-size: 15px;

  .overall-compliance {
    margin-bottom: 10px;
    background: white;
    border-radius: 15px;
    padding: 10px 15px;
    text-align: center;
    font-weight: bold;
    color: #414141;
    display: flex;
    align-items: center;
    width: 100%;
    gap: 5px;

    .link {
      color: #414141;
      font-weight: bold;
      margin-right: 5px;
      text-decoration: underline;
      cursor: pointer;
      font-size: 12px;

      &:hover {
        color: #131313;
      }
    }
  }
  @media only screen and (max-width: 600px), screen and (max-height: 550px) {
    border-radius: 10px;
    font-size: 12px;
    max-width: 35%;
  }

  @media only screen and (max-width: 500px), screen and (max-height: 400px) {
    border-radius: 5px;
    font-size: 10px;
    padding: 8px 10px;
    max-width: 35%;
  }

  &.fade-out {
    .compliance-control-item,
    .overall-compliance {
      animation: fadeOut 1s;
      opacity: $base-opacity;
    }
  }

  &.fade-in {
    .compliance-control-item {
      animation: fadeIn 1s;
      opacity: $top-opacity;
    }
    .overall-compliance {
      animation: fadeInFull 1s;
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      opacity: $top-opacity;
    }
    to {
      opacity: $base-opacity;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: $base-opacity;
    }
    to {
      opacity: $top-opacity;
    }
  }
  @keyframes fadeInFull {
    from {
      opacity: $base-opacity;
    }
    to {
      opacity: 1;
    }
  }
}
.compliance-logger-container {
  overflow: scroll;
  // Hide scrollbar
  -ms-overflow-style: none;
  scrollbar-width: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  .compliance-control-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .full-info-label-list {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
  }

  .no-underline {
    text-decoration: none;
  }
  .link {
    color: #414141;
    font-weight: bold;
    margin-right: 5px;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #131313;
    }
  }

  .overflow-title {
    color: white;
    font-weight: bold;
    font-size: 15px;
  }

  .compliance-control-item {
    margin-bottom: 10px;
    background: white;
    padding: 10px 15px;
    border-radius: 15px;
    font-size: 12px;
    opacity: $top-opacity;
    text-align: left;
    text-shadow: none;
    color: black;
    line-height: initial;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &:hover {
      opacity: 1;
    }

    &.is-dark {
      background: gray;
      color: white;

      .compliance-rm {
        color: white;
      }
    }

    .icon-link {
      cursor: pointer;
    }

    .readable-title {
      font-weight: bold;
      font-size: 15px;
      color: #414141;
      width: 90%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .compliance-rm {
      font-weight: bold;
      font-size: 15px;
      color: #414141;

      .searchable {
        display: flex;

        .should-link {
          color: #414141;
          font-weight: bold;
          font-size: 15px;
          margin-right: 5px;

          &:hover {
            color: #131313;
          }
        }
        .person-badge {
          color: #414141;
          cursor: pointer;
          align-items: center;
          display: flex;

          &:hover {
            color: #131313;
          }
        }
      }
    }

    .date {
      font-weight: bold;
    }

    @media only screen and (max-width: 600px), screen and (max-height: 550px) {
      border-radius: 10px;
      font-size: 10px;
      .compliance-rm {
        font-size: 12px;
      }

      .readable-title {
        font-size: 12px;
        width: 80%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    @media only screen and (max-width: 500px), screen and (max-height: 400px) {
      border-radius: 5px;
      font-size: 8px;
      padding: 8px 10px;
      .compliance-rm {
        font-size: 10px;
      }
    }
  }
}
</style>
<!----------------- END SCSS ---------------------->
